import { FiTaskV2AssigneesHelper } from './../../fi-task-v2-assignees-helper';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { ITaskV2, ITaskV2AssignedUser } from '../../fi-task-v2';
import { IAccessRightFilterCollection } from '@fi-shared/models';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { Animations } from '@fi-shared/utils/animations';
import { SharedActionsBaseService } from '@fi-shared/fi-shared-services/shared-actions-base.service';
import { FiTaskV2AssigneeHelperService } from './fi-task-v2-assignee-helper.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiTaskV2Service } from '@fi-app/task-v2/fi-task-v2.service';
import { FiTaskV2HelperService } from '@fi-app/task-v2/fi-task-v2-helper.service';
import { FiTaskSyncService } from '@fi-shared/fi-task-shared/fi-task-sync-service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'fi-task-v2-assignee',
    templateUrl: './task-v2-assignee.component.html',
    styleUrls: ['./task-v2-assignee.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-task-v2-assignee' },
    standalone: false,
    animations: [Animations.fadeHeight()],
})
export class TaskV2AssigneeComponent implements OnInit, OnDestroy {
    @Input({ required: true }) task: ITaskV2;
    @Input({ required: true }) availableAccessRightFilterCollection: IAccessRightFilterCollection;
    @Output() taskAssigneesChange = new EventEmitter<void>();
    @Input() isAutomatedTask: boolean = false;

    readonly maxDisplayedAssigneeItems = 4;
    protected readonly FiProfileSizes = FiProfileSizes;
    protected fiTaskV2AssigneesHelper: FiTaskV2AssigneesHelper;
    private _availableUsersForAssignee: ITaskV2AssignedUser[] = [];

    constructor(
        private sharedActionsService: SharedActionsBaseService,
        private fiTaskV2Service: FiTaskV2Service,
        private assigneeHelperService: FiTaskV2AssigneeHelperService,
        private fiTaskV2HelperService: FiTaskV2HelperService,
        private appTextService: AppTextService,
        private fiTaskSyncService: FiTaskSyncService,
        private cdr: ChangeDetectorRef,
    ) {}

    protected get canAddNewAssignee(): boolean {
        return this.fiTaskV2HelperService.canAddAssignee(this.task.CreatedByUserId, this.task.IsArchived);
    }

    ngOnInit(): void {
        this.setAssigneesHelper();
        this.subscribeToSyncTasks();
    }

    private setAssigneesHelper() {
        this.fiTaskV2AssigneesHelper = new FiTaskV2AssigneesHelper(
            this.appTextService,
            this.task.AccessRightFilterCollection,
            this.task.AssignedUsers,
            this.task.AssignmentType,
        );
    }

    private subscribeToSyncTasks() {
        this.fiTaskSyncService.subscribeToTaskUpdatedBySync(this.task.Id, this.ngOnDestroy$, (updatedTask) => {
            this.task.AssignedUsers = updatedTask.AssignedUsers;
            this.task.AccessRightFilterCollection = updatedTask.AccessRightFilterCollection;
            this.setAssigneesHelper();
            this.cdr.markForCheck();
        });
    }

    protected getCardHeaderText(): string {
        return this.assigneeHelperService.getCardHeaderText(this.fiTaskV2AssigneesHelper.isRoleUnitAssignees);
    }

    protected openAssignedStaffMembersViewer(): void {
        this.assigneeHelperService.openViewStaffMembersAssigneeDialog(
            this.task?.AssignedUsers,
            this.fiTaskV2AssigneesHelper.assignedRoles,
            this.fiTaskV2AssigneesHelper.assignedBusinessUnits,
        );
    }

    protected openAddStaffMembersAssigneeDialog(): void {
        this.getAvailableUsersForAssignee().then((availableUsersForAssignee) => {
            this.assigneeHelperService
                .openAddStaffMembersAssigneeDialog(this.task?.AssignedUsers, availableUsersForAssignee)
                .onYesButtonClick((newAssigneeIds: number[]) => {
                    this.addTaskAssignees(newAssigneeIds);
                });
        });
    }

    protected openUserDetails(userId: number): void {
        this.sharedActionsService.openUserViewPopup(userId);
    }

    protected openBusinessUnitDetails(businessUnitId: number): void {
        this.sharedActionsService.openUnitViewPopup(businessUnitId);
    }

    private getAvailableUsersForAssignee(): Promise<ITaskV2AssignedUser[]> {
        return new Promise<ITaskV2AssignedUser[]>((resolve, reject) => {
            if (!!this._availableUsersForAssignee?.length) {
                resolve(this._availableUsersForAssignee);
                return;
            }

            this.setAvailableUsersForAssignee(resolve, reject);
        });
    }

    private setAvailableUsersForAssignee(
        resolve: (users: ITaskV2AssignedUser[]) => void,
        reject: (reason?: any) => void,
    ): void {
        this.fiTaskV2Service.getAvailableUsersForAssignee().then((availableUsersForAssignee) => {
            this._availableUsersForAssignee = availableUsersForAssignee;
            resolve(this._availableUsersForAssignee);
        }, reject);
    }

    private addTaskAssignees(userIds: number[]): void {
        this.fiTaskV2Service.addTaskAssignees(this.task.Id, userIds).then(() => {
            const addedUsers = this._availableUsersForAssignee.filter((user) => userIds.includes(user.Id));
            this.task.AssignedUsers = [...addedUsers, ...(this.task.AssignedUsers || [])];
            this.taskAssigneesChange.emit();
        });
    }

    private ngOnDestroy$ = new Subject<void>();

    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
